import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { Copy, Mail, MessageCircleHeart } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useUserStore } from 'stores/user';
import { copyToClipboard, openInNewTab } from 'utils/common';
import { buildReferralLink } from 'utils/referral';
import { WhatsAppIcon } from './WhatsAppIcon';
import { referralCardStyles } from './styles';

export const ReferralCard = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.HOME);
  const user = useUserStore(state => state.user);
  const promoterId = user?.id?.trim();
  const referralLink = buildReferralLink({ promoterId });
  const referralMessage = `${t('Pae.ReferralCard.title')}\n${referralLink}`;

  const handleCopyLink = (): void => {
    copyToClipboard(referralLink);
    toast.success(t('Pae.ReferralCard.copySuccess'));
  };

  const handleShareByEmail = (): void => {
    const subject = encodeURIComponent(t('Pae.ReferralCard.title'));
    const body = encodeURIComponent(referralMessage);
    openInNewTab(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleShareByWhatsApp = (): void => {
    openInNewTab(`https://wa.me/?text=${encodeURIComponent(referralMessage)}`);
  };

  const copyButtonLabel = (
    <>
      <Copy className={referralCardStyles.copyButtonIcon} />
      {t('Pae.ReferralCard.copyButton')}
    </>
  );

  return (
    <Card className={referralCardStyles.card}>
      <div className={referralCardStyles.content}>
        <MessageCircleHeart aria-hidden className={referralCardStyles.heroIcon} strokeWidth={0.9} />
        <div className={referralCardStyles.intro}>
          <h2 className={referralCardStyles.title}>{t('Pae.ReferralCard.title')}</h2>
          <p className={cn('w-full', referralCardStyles.bodyText)}>
            <Trans
              i18nKey="Pae.ReferralCard.description"
              ns={LOCIZE_NS.HOME}
              components={{
                discount: <span className={cn('font-semibold', referralCardStyles.bodyText)} />,
              }}
            />
          </p>
        </div>
        <div className={referralCardStyles.actions}>
          <div className={referralCardStyles.linkRow}>
            <input readOnly value={referralLink} className={referralCardStyles.linkInput} />
            <Button
              type="button"
              variant="secondary"
              size="md"
              icon="leading"
              onClick={handleCopyLink}
              className={cn(
                referralCardStyles.copyButton,
                'hidden shrink-0 am-3xl:mr-2 am-3xl:inline-flex'
              )}
            >
              {copyButtonLabel}
            </Button>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="md"
            icon="leading"
            onClick={handleCopyLink}
            className={cn(referralCardStyles.copyButton, 'am-3xl:hidden')}
          >
            {copyButtonLabel}
          </Button>
          <div className={referralCardStyles.shareRow}>
            <Button
              type="button"
              variant="terciary"
              size="md"
              icon="leading"
              className={referralCardStyles.shareButton}
              onClick={handleShareByEmail}
            >
              <Mail className={referralCardStyles.shareButtonIcon} />
              {t('Pae.ReferralCard.emailButton')}
            </Button>
            <Button
              type="button"
              variant="terciary"
              size="md"
              icon="leading"
              className={cn(referralCardStyles.shareButton, referralCardStyles.whatsAppShareButton)}
              onClick={handleShareByWhatsApp}
            >
              <WhatsAppIcon className={referralCardStyles.whatsAppIcon} />
              {t('Pae.ReferralCard.whatsappButton')}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
